import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Truck, Banknote, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { resolveImage, formatPrice, formatApiErrorDetail } from "@/lib/api";

export default function Checkout() {
  const { items, subtotal, shipping, total, clear } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", address: "", city: "", county: "", postal_code: "", notes: "",
  });

  useEffect(() => {
    if (items.length === 0 && !submitting) navigate("/cos");
  }, [items, submitting, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address || !form.city || !form.county) {
      toast.error("Completează toate câmpurile obligatorii");
      return;
    }
    if (!/^07\d{8}$/.test(form.phone)) {
      toast.error("Numărul de telefon trebuie să înceapă cu 07 și să aibă 10 cifre");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, name: i.name, price: i.price, qty: i.qty, image: i.image })),
        customer: { ...form, email: form.email || user?.email || "" },
      });
      if (window.fbq) {
        window.fbq("track", "Purchase", {
          value: Number(data.total ?? total),
          currency: "RON",
          content_type: "product",
          contents: items.map((i) => ({ id: i.product_id, quantity: i.qty, item_price: i.price })),
          num_items: items.reduce((n, i) => n + i.qty, 0),
        }, { eventID: `purchase_${data.id}` });
      }
      clear();
      navigate(`/comanda/${data.id}`, { state: { justPlaced: true } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Eroare la plasarea comenzii");
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" data-testid="checkout-page">
      <h1 className="mb-8 font-heading text-3xl font-bold tracking-tight text-slate-900">Finalizare comandă</h1>
      {!user && (
        <div className="mb-6 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="guest-notice">
          Comanzi în calitate de vizitator — nu ai nevoie de cont.
          <Link to="/autentificare" state={{ from: { pathname: "/finalizare" } }} className="font-medium text-primary hover:underline" data-testid="checkout-login-link">
            Ai deja cont? Autentifică-te
          </Link>
        </div>
      )}
      <form onSubmit={submit} className="grid gap-10 lg:grid-cols-3" autoComplete="off">
        <div className="space-y-6 lg:col-span-2">
          <div className="rounded-xl border border-slate-200 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-900">Date de livrare</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label htmlFor="name">Nume complet *</Label>
                <Input id="name" value={form.name} onChange={set("name")} className="mt-1.5" data-testid="checkout-name" autoComplete="off" required />
              </div>
              <div>
                <Label htmlFor="phone">Telefon *</Label>
                <Input id="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="mt-1.5" data-testid="checkout-phone" inputMode="numeric" maxLength={10} placeholder="07xxxxxxxx" autoComplete="off" required />
              </div>
              <div>
                <Label htmlFor="email">Email (opțional)</Label>
                <Input id="email" type="email" value={form.email} onChange={set("email")} className="mt-1.5" data-testid="checkout-email" autoComplete="off" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="address">Adresă (stradă, număr) *</Label>
                <Input id="address" value={form.address} onChange={set("address")} className="mt-1.5" data-testid="checkout-address" autoComplete="off" required />
              </div>
              <div>
                <Label htmlFor="city">Oraș sau sat *</Label>
                <Input id="city" value={form.city} onChange={set("city")} className="mt-1.5" data-testid="checkout-city" autoComplete="off" required />
              </div>
              <div>
                <Label htmlFor="county">Județ *</Label>
                <Input id="county" value={form.county} onChange={set("county")} className="mt-1.5" data-testid="checkout-county" autoComplete="off" required />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="notes">Observații (opțional)</Label>
                <Textarea id="notes" value={form.notes} onChange={set("notes")} className="mt-1.5" data-testid="checkout-notes" autoComplete="off" />
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-900">Metodă de plată</h2>
            <div className="mt-4 flex items-center gap-3 rounded-lg border-2 border-primary bg-primary/5 p-4">
              <Banknote className="h-6 w-6 text-primary" />
              <div>
                <p className="font-medium text-slate-900">Plată la livrare (ramburs)</p>
                <p className="text-sm text-slate-500">Plătești în numerar când primești coletul.</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <div className="sticky top-24 rounded-xl border border-slate-200 bg-slate-50 p-6">
            <h2 className="font-heading text-lg font-bold text-slate-900">Comanda ta</h2>
            <div className="mt-4 space-y-3">
              {items.map((i) => (
                <div key={i.product_id} className="flex items-center gap-3">
                  <img src={resolveImage(i.image)} alt={i.name} className="h-12 w-12 rounded border border-slate-200 object-contain p-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="line-clamp-1 font-medium text-slate-800">{i.name}</p>
                    <p className="text-slate-500">{i.qty} × {formatPrice(i.price)}</p>
                  </div>
                  <span className="text-sm font-medium">{formatPrice(i.price * i.qty)}</span>
                </div>
              ))}
            </div>
            <Separator className="my-4" />
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span>{formatPrice(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Transport</span><span className="font-semibold text-success">GRATUIT</span></div>
              <Separator />
              <div className="flex justify-between text-base"><span className="font-semibold">Total</span><span className="font-heading font-bold text-primary">{formatPrice(total)}</span></div>
            </div>
            <Button type="submit" disabled={submitting} className="mt-6 w-full rounded-full hover:bg-primary/90" size="lg" data-testid="place-order-button">
              {submitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Se plasează...</> : "Plasează comanda"}
            </Button>
            <p className="mt-3 flex items-center justify-center gap-1 text-xs text-slate-500">
              <ShieldCheck className="h-3.5 w-3.5" /> Comandă securizată
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
